import React, { useState, useEffect } from 'react';
import AppLayout from '../../components/AppLayout';
import ProspectingNav from '../../components/prospecting/ProspectingNav';
import { useTheme } from '../../contexts/ThemeContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, Filter, Plus, Trash2, ChevronLeft, ChevronRight, ExternalLink, ArrowUpDown, X, MoreHorizontal, Send } from 'lucide-react';
import { PIPELINE_PIPELINE_STAGES } from '../../components/prospecting/constants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LeadPipeline() {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState(searchParams.get('stage') || '');
  const [selected, setSelected] = useState([]);
  const [showAddLead, setShowAddLead] = useState(false);
  const [newLead, setNewLead] = useState({ first_name: '', last_name: '', email: '', company: '', title: '', linkedin_url: '' });
  const [showEnroll, setShowEnroll] = useState(false);
  const [sequences, setSequences] = useState([]);
  const [enrollSeqId, setEnrollSeqId] = useState('');
  const [enrolling, setEnrolling] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchLeads(); }, [page, stageFilter, search]);

  const fetchLeads = async () => {
    setLoading(true);
    const params = new URLSearchParams({ page: page.toString(), limit: '50' });
    if (stageFilter) params.set('stage', stageFilter);
    if (search) params.set('search', search);
    try {
      const res = await fetch(`${BACKEND_URL}/api/prospecting/leads?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setLeads(data.leads);
        setTotal(data.total);
        setPages(data.pages);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleStageChange = async (leadId, newStage) => {
    await fetch(`${BACKEND_URL}/api/prospecting/leads/${leadId}/stage`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ stage: newStage }),
    });
    fetchLeads();
  };

  const handleBulkDelete = async () => {
    if (!selected.length || !window.confirm(`Delete ${selected.length} leads?`)) return;
    await fetch(`${BACKEND_URL}/api/prospecting/leads/bulk-action`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ lead_ids: selected, action: 'delete' }),
    });
    setSelected([]);
    fetchLeads();
  };

  const handleAddLead = async () => {
    const body = {
      linkedin: { first_name: newLead.first_name, last_name: newLead.last_name, current_company: newLead.company, current_title: newLead.title, profile_url: newLead.linkedin_url },
      contact: { email: newLead.email },
      stage: 'imported',
    };
    const res = await fetch(`${BACKEND_URL}/api/prospecting/leads`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify(body),
    });
    if (res.ok) { setShowAddLead(false); setNewLead({ first_name: '', last_name: '', email: '', company: '', title: '', linkedin_url: '' }); fetchLeads(); }
  };

  const openEnrollModal = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/prospecting/sequences`, { credentials: 'include' });
      if (res.ok) setSequences(await res.json());
    } catch (err) { console.error(err); }
    setShowEnroll(true);
  };

  const handleEnroll = async () => {
    if (!enrollSeqId || !selected.length) return;
    setEnrolling(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/prospecting/sequences/${enrollSeqId}/enroll`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ lead_ids: selected }),
      });
      if (res.ok) {
        const result = await res.json();
        alert(`Enrolled ${result.enrolled} leads. Skipped: ${result.skipped_opt_out} opt-outs, ${result.skipped_already_enrolled} already enrolled.`);
        setSelected([]);
        setShowEnroll(false);
        setEnrollSeqId('');
        fetchLeads();
      }
    } catch (err) { console.error(err); }
    finally { setEnrolling(false); }
  };

  const toggleSelect = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAll = () => setSelected(selected.length === leads.length ? [] : leads.map(l => l.lead_id));

  const card = `${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'} border rounded-xl`;
  const input = `w-full px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} border`;

  return (
    <AppLayout title="Lead Pipeline">
      <div className="space-y-4" data-testid="lead-pipeline">
        <ProspectingNav />
        <div className="flex items-center justify-between">
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Lead Pipeline</h1>
          <div className="flex gap-2">
            {selected.length > 0 && (
              <>
                <button onClick={openEnrollModal} data-testid="quick-enroll-btn" className="flex items-center gap-1 px-3 py-2 rounded-lg bg-purple-600 text-white text-sm hover:bg-purple-700">
                  <Send className="w-4 h-4" /> Enroll ({selected.length})
                </button>
                <button onClick={handleBulkDelete} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700">
                  <Trash2 className="w-4 h-4" /> Delete ({selected.length})
                </button>
              </>
            )}
            <button onClick={() => setShowAddLead(true)} data-testid="add-lead-btn" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#A2FF00] text-black font-medium hover:bg-[#8FE600] text-sm">
              <Plus className="w-4 h-4" /> Add Lead
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <input
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search leads..."
              className={`pl-10 ${input}`}
              data-testid="search-leads"
            />
          </div>
          <select
            value={stageFilter}
            onChange={e => { setStageFilter(e.target.value); setPage(1); }}
            className={`px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300'} border`}
            data-testid="stage-filter"
          >
            <option value="">All Stages</option>
            {PIPELINE_STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>

        {/* Table */}
        <div className={`${card} overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="leads-table">
              <thead>
                <tr className={`border-b ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                  <th className="p-3 w-10"><input type="checkbox" checked={leads.length > 0 && selected.length === leads.length} onChange={toggleAll} className="rounded" /></th>
                  <th className={`text-left p-3 text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Name</th>
                  <th className={`text-left p-3 text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Company</th>
                  <th className={`text-left p-3 text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Title</th>
                  <th className={`text-left p-3 text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Stage</th>
                  <th className={`text-left p-3 text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Score</th>
                  <th className={`text-left p-3 text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Email</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="7" className="p-8 text-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#A2FF00] mx-auto"></div></td></tr>
                ) : leads.length === 0 ? (
                  <tr><td colSpan="7" className={`p-8 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No leads found</td></tr>
                ) : leads.map(lead => {
                  const stage = PIPELINE_STAGES.find(s => s.key === lead.stage) || { label: lead.stage, color: 'bg-gray-500' };
                  return (
                    <tr key={lead.lead_id} className={`border-b ${isDark ? 'border-white/5 hover:bg-white/5' : 'border-gray-100 hover:bg-gray-50'} cursor-pointer`} data-testid={`lead-row-${lead.lead_id}`}>
                      <td className="p-3" onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={selected.includes(lead.lead_id)} onChange={() => toggleSelect(lead.lead_id)} className="rounded" />
                      </td>
                      <td className={`p-3 text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`} onClick={() => navigate(`/lead-finder/leads/${lead.lead_id}`)}>
                        {lead.linkedin?.first_name || ''} {lead.linkedin?.last_name || ''}
                      </td>
                      <td className={`p-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{lead.linkedin?.current_company || '-'}</td>
                      <td className={`p-3 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{lead.linkedin?.current_title || '-'}</td>
                      <td className="p-3" onClick={e => e.stopPropagation()}>
                        <select
                          value={lead.stage}
                          onChange={e => handleStageChange(lead.lead_id, e.target.value)}
                          className={`px-2 py-1 rounded text-xs font-medium ${stage.color} text-white border-none cursor-pointer`}
                        >
                          {PIPELINE_STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                        </select>
                      </td>
                      <td className={`p-3 text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {lead.scoring?.score != null ? lead.scoring.score : '-'}
                      </td>
                      <td className={`p-3 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{lead.contact?.email || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className={`flex items-center justify-between p-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{total} leads total</span>
              <div className="flex gap-2">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className={`p-2 rounded ${isDark ? 'hover:bg-white/10 disabled:opacity-30' : 'hover:bg-gray-100 disabled:opacity-30'}`}><ChevronLeft className="w-4 h-4" /></button>
                <span className={`px-3 py-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{page} / {pages}</span>
                <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} className={`p-2 rounded ${isDark ? 'hover:bg-white/10 disabled:opacity-30' : 'hover:bg-gray-100 disabled:opacity-30'}`}><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}
        </div>

        {/* Add Lead Modal */}
        {showAddLead && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className={`w-full max-w-lg rounded-2xl p-6 ${isDark ? 'bg-[#111] border border-white/10' : 'bg-white'}`}>
              <div className="flex items-center justify-between mb-4">
                <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Add Lead Manually</h2>
                <button onClick={() => setShowAddLead(false)} className={`p-1 rounded ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <input value={newLead.first_name} onChange={e => setNewLead(p => ({ ...p, first_name: e.target.value }))} placeholder="First Name *" className={input} data-testid="lead-first-name" />
                  <input value={newLead.last_name} onChange={e => setNewLead(p => ({ ...p, last_name: e.target.value }))} placeholder="Last Name" className={input} data-testid="lead-last-name" />
                </div>
                <input value={newLead.email} onChange={e => setNewLead(p => ({ ...p, email: e.target.value }))} placeholder="Email" className={input} data-testid="lead-email" />
                <input value={newLead.company} onChange={e => setNewLead(p => ({ ...p, company: e.target.value }))} placeholder="Company" className={input} />
                <input value={newLead.title} onChange={e => setNewLead(p => ({ ...p, title: e.target.value }))} placeholder="Job Title" className={input} />
                <input value={newLead.linkedin_url} onChange={e => setNewLead(p => ({ ...p, linkedin_url: e.target.value }))} placeholder="LinkedIn URL" className={input} />
              </div>
              <div className="flex justify-end gap-3 mt-5">
                <button onClick={() => setShowAddLead(false)} className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'}`}>Cancel</button>
                <button onClick={handleAddLead} data-testid="save-lead-btn" className="px-4 py-2 rounded-lg text-sm bg-[#A2FF00] text-black font-medium hover:bg-[#8FE600]">Add Lead</button>
              </div>
            </div>
          </div>
        )}

        {/* Quick Enroll Modal */}
        {showEnroll && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className={`w-full max-w-md rounded-2xl p-6 ${isDark ? 'bg-[#111] border border-white/10' : 'bg-white'}`}>
              <div className="flex items-center justify-between mb-4">
                <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Enroll {selected.length} Lead{selected.length !== 1 ? 's' : ''} in Sequence</h2>
                <button onClick={() => setShowEnroll(false)} className={`p-1 rounded ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}><X className="w-5 h-5" /></button>
              </div>
              {sequences.length > 0 ? (
                <div className="space-y-3">
                  <select value={enrollSeqId} onChange={e => setEnrollSeqId(e.target.value)} className={input} data-testid="enroll-sequence-select">
                    <option value="">-- Select Sequence --</option>
                    {sequences.map(s => (
                      <option key={s.sequence_id} value={s.sequence_id}>{s.name} ({s.steps?.length || 0} steps)</option>
                    ))}
                  </select>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Leads already enrolled or opted-out will be skipped automatically.
                  </p>
                  <div className="flex justify-end gap-3">
                    <button onClick={() => setShowEnroll(false)} className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'}`}>Cancel</button>
                    <button onClick={handleEnroll} disabled={!enrollSeqId || enrolling} data-testid="confirm-enroll-btn" className="px-4 py-2 rounded-lg text-sm bg-purple-600 text-white font-medium hover:bg-purple-700 disabled:opacity-50">
                      {enrolling ? 'Enrolling...' : 'Enroll'}
                    </button>
                  </div>
                </div>
              ) : (
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No sequences available. Create a sequence first.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
